import { defineFeature } from '../define-feature';
import { TEST_IDS, fieldTestId, optionTestId, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'customers.customer.create',
  category: 'customers',
  title: 'Créer un client',
  intent:
    'Ajouter une nouvelle fiche client à votre base pour pouvoir la rattacher ensuite à des rendez-vous, des campagnes marketing et toutes vos communications — le point de départ de toute la relation client dans Propelsia.',
  context: {
    why: "Votre base clients est l'un des actifs les plus précieux de votre entreprise. Chaque fiche client bien renseignée devient un point d'ancrage pour construire une vraie relation dans la durée : elle vous permet de personnaliser vos communications, de suivre l'historique des rendez-vous, de segmenter vos campagnes marketing par tag, de proposer les bonnes prestations aux bons profils. Une base client mal tenue, à l'inverse, se paie sur le long terme : messages qui partent à la mauvaise adresse, doublons, difficulté à identifier vos meilleurs clients, campagnes marketing peu ciblées. Propelsia a fait le choix de la simplicité maximale à la création : il suffit que le client soit identifiable, par un e-mail, un téléphone, ou son prénom et son nom. Vous pouvez donc créer une fiche client en dix secondes chrono si nécessaire, même sans e-mail, quitte à compléter les autres informations plus tard. Cette souplesse évite de bloquer votre workflow au moment critique — un client au téléphone, un walk-in au comptoir, une prise de rendez-vous rapide. Vous avez deux points d'entrée pour créer un client : depuis la rubrique Clients pour une gestion administrative classique, ou directement depuis le formulaire de rendez-vous pour une création à la volée pendant la prise de RDV, sans quitter l'agenda.",
    use_cases: [
      "Un nouveau client vous appelle pour prendre son premier rendez-vous : créez sa fiche à la volée pendant l'appel, sans le faire attendre.",
      'Vous récupérez une liste de contacts depuis un ancien outil ou un fichier Excel : créez les fiches une par une pour intégrer votre base historique.',
      "Un client walk-in se présente au comptoir : créez sa fiche pour l'intégrer à votre base et lui envoyer un rappel avant le prochain rendez-vous.",
      'Vous voulez créer une fiche fictive pour un test ou une formation : la souplesse du formulaire vous le permet en quelques secondes.',
      "Un membre de l'équipe rencontre un prospect en dehors du cabinet et veut l'ajouter à la base : la création se fait depuis mobile en quelques champs.",
    ],
  },
  required_scopes: ['customer.create'],
  ui_entry_points: [
    { route: '/customers', element: 'Bouton « Ajouter + » en haut de la liste des clients' },
    { route: '/agenda', element: 'Bouton de création rapide dans le formulaire de rendez-vous' },
  ],
  api: {
    primary_operation_id: 'ProCustomerAppController_createProCustomer',
    related_operation_ids: ['ProCustomerAppController_getProCustomer'],
  },
  steps: [
    'Ouvrir la rubrique Clients, cliquer sur « Ajouter + » puis choisir « Ajouter + » dans le menu. L’autre entrée du même menu, « Importer des clients », lance l’import d’un fichier.',
    'Renseigner au minimum un e-mail, un téléphone, ou le prénom et le nom.',
    'Compléter nom, prénom, téléphone, adresse et tags selon les besoins.',
    'Enregistrer — le client apparaît dans la liste.',
  ],
  gotchas: [
    'Il faut au moins un e-mail, un téléphone, ou le prénom et le nom ; tout le reste est facultatif. Un client sans e-mail ne reçoit ni confirmation de rendez-vous ni campagne e-mail ; avec un téléphone, il reçoit ses rappels par SMS (si vous les avez activés).',
    "Un client peut aussi être créé à la volée depuis le formulaire de rendez-vous, sans quitter l'agenda.",
  ],
  related_features: ['agenda.appointment.create'],
  demo_script: {
    duration_target_s: 35,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          'Chaque fiche client devient le point de départ de toute votre relation : voyons comment en créer une.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/customers'),
        caption: 'Menu latéral, Clients : c’est ici que toute votre base se gère.',
      },
      { action: 'click', target: TEST_IDS.customers_add, caption: 'On lance la création avec « Ajouter ».' },
      // Le bouton « Ajouter + » ouvre désormais un menu (création manuelle /
      // import de fichier) : la création est la première option.
      { action: 'click', target: optionTestId(TEST_IDS.customers_add, 0) },
      {
        action: 'wait_for',
        target: fieldTestId('email'),
        caption: 'Le formulaire s’ouvre : un e-mail, un téléphone ou un nom complet suffit.',
      },
      {
        action: 'fill',
        target: fieldTestId('email'),
        value: 'juliette.moreau@exemple.fr',
        caption: 'On renseigne l’e-mail, pour envoyer confirmations et rappels.',
      },
      {
        action: 'fill',
        target: fieldTestId('firstname'),
        value: 'Juliette',
        caption: 'Le prénom, pour personnaliser ensuite vos échanges.',
      },
      {
        action: 'fill',
        target: fieldTestId('lastname'),
        value: 'Moreau',
        caption: 'Le nom complète la fiche : tout le reste peut se remplir plus tard.',
        capture: true,
      },
      { action: 'click', target: TEST_IDS.modal_submit, caption: 'On enregistre.' },
      {
        action: 'expect_visible',
        target: TEST_IDS.customers_add,
        caption: 'Votre client est dans la base, prêt à être rattaché à un rendez-vous ou à une campagne.',
        capture: true,
      },
    ],
  },
});
